/**
 * Per-app pay plan keys (must match backend PayPlanAppCode / cloud_user.pay_plans JSON).
 */
export const PAY_PLAN_APP = {
    WEB: "web",
    TUTOR: "tutor",
    SLIDING_PUZZLE_QUEST: "sliding_puzzle_quest",
    JIGSAW_MUSE: "jigsaw_muse",
    WORD_WHEEL_QUEST: "word_wheel_quest",
};

/**
 * Web credit / coin / assistant API {@code appCode} ({@code mo_apps.code}).
 * Distinct from pay-plan key {@link PAY_PLAN_APP.WEB} (`web`).
 */
export const WEB_APP_CODE = "web_app";

/** Web ghost-guide hint credits ({@code mo_apps.code}). */
export const WEB_APP_HINTS_CODE = "web_app_hints";


/** Short labels for admin account grid. */
export const PAY_PLAN_APP_LABELS = {
    [PAY_PLAN_APP.WEB]: "Web",
    [PAY_PLAN_APP.TUTOR]: "Tutor",
    [PAY_PLAN_APP.SLIDING_PUZZLE_QUEST]: "Sliding",
    [PAY_PLAN_APP.JIGSAW_MUSE]: "Jigsaw",
    [PAY_PLAN_APP.WORD_WHEEL_QUEST]: "Word Wheel",
};

/** Apps shown on admin user list (web + tutor + mobile). */
export const PAY_PLAN_APPS_FOR_ADMIN = [
    PAY_PLAN_APP.WEB,
    PAY_PLAN_APP.TUTOR,
    PAY_PLAN_APP.SLIDING_PUZZLE_QUEST,
    PAY_PLAN_APP.JIGSAW_MUSE,
    PAY_PLAN_APP.WORD_WHEEL_QUEST,
];

export const SIGNUP_SOURCE_LABELS = {
    WEB: "Web",
    IOS: "iOS",
    ANDROID: "Android",
    APPLE: "Apple",
    GOOGLE: "Google",
    UNKNOWN: "Unknown",
};

function readPayPlans(cloudUser) {
    if (!cloudUser) {
        return null;
    }
    const plans = cloudUser.payPlans ?? cloudUser.pay_plans;
    if (plans && typeof plans === "object") {
        return plans;
    }
    return null;
}

/**
 * Resolve entitlement for a specific app from GET /home/user or admin user payload.
 *
 * @param {object | null | undefined} cloudUser
 * @param {string} [appCode=PAY_PLAN_APP.WEB]
 * @returns {string} FREE | STANDARD | PREMIUM
 */
export function resolvePayPlan(cloudUser, appCode = PAY_PLAN_APP.WEB) {
    const plans = readPayPlans(cloudUser);
    if (!plans) {
        return "FREE";
    }
    if (plans[appCode]) {
        return plans[appCode];
    }
    if (appCode === PAY_PLAN_APP.JIGSAW_MUSE && plans.jigsaw_puzzle_master) {
        return plans.jigsaw_puzzle_master;
    }
    return "FREE";
}

export function isStandardOrPremiumPlan(plan) {
    return plan === "STANDARD" || plan === "PREMIUM";
}

export function isStandardPayPlanForUser(cloudUser, appCode = PAY_PLAN_APP.WEB) {
    return isStandardOrPremiumPlan(resolvePayPlan(cloudUser, appCode));
}

/** Active Tutor Standard subscription (`pay_plans.tutor = STANDARD`). */
export function isTutorStandardUser(cloudUser) {
    return resolvePayPlan(cloudUser, PAY_PLAN_APP.TUTOR) === "STANDARD";
}

/**
 * Support perk: Starter Fun Bundle / Classic Challenge unlock web PREMIUM.
 * Coin pack and Tutor Standard do not. Staff (ADMIN) can open Support for testing.
 */
export function hasWebPremiumSupportAccess(cloudUser) {
    if (!cloudUser) return false;
    const role = String(cloudUser.userRole || cloudUser.user_role || "").toUpperCase();
    if (role === "ADMIN") return true;
    return resolvePayPlan(cloudUser, PAY_PLAN_APP.WEB) === "PREMIUM";
}

/** Chip color for FREE / STANDARD / PREMIUM in admin UI. */
export function payPlanChipColor(plan) {
    if (plan === "PREMIUM") return "secondary";
    if (plan === "STANDARD") return "primary";
    return "default";
}
